/**
 * O que muda entre os recursos de coleta (techspec-coleta §07, techspec-mineracao §07): o texto, o
 * ícone e a ferramenta. A janela, o cartão do Viajar e a regra são os mesmos — a mineração é a coleta
 * de madeira com outro recurso (Renato, 16/10: "as mesmas infos que temos no lumber").
 */
export type RecursoColeta = 'madeira' | 'minerio';

export interface TextosDoRecurso {
  /** Título da janela. */
  readonly titulo: string;
  /** O lugar, enquanto o estado do servidor não chegou. */
  readonly lugar: string;
  /** "Árvore {n}" / "Veio {n}". */
  readonly ponto: string;
  /** "lenhadores" / "mineradores". */
  readonly coletores: string;
  readonly faseAndando: string;
  readonly faseCortando: string;
  readonly resIda: string;
  readonly resProcurando: string;
  readonly resAndando: string;
  readonly resErro: string;
  readonly travado: string;
  /** O nome da skill na régua. */
  readonly skill: string;
  readonly proxima: string;
  readonly todas: string;
  readonly ferramentaDica: string;
  readonly cofreDica: string;
  readonly itensChance: string;
  readonly trancada: string;
  readonly bonus: string;
  readonly vjLugar: string;
  readonly vjAquiPonto: string;
  readonly temFerramentaBonus: string;
  readonly semFerramenta: string;
  readonly resumo: string;
  /** Viajar → Recursos: o nome da linha. */
  readonly linha: string;
}

export interface Recurso {
  readonly id: RecursoColeta;
  /** O item comum que sai (o ícone do cofre e da linha do Viajar). */
  readonly itemComum: string;
  /** A ferramenta comum, quando o personagem não tem nenhuma. */
  readonly ferramentaPadrao: string;
  /** Cor da marca de cada item na régua da skill (o comum não tem marca). */
  readonly cores: Readonly<Record<string, string>>;
  readonly t: TextosDoRecurso;
}

const MADEIRA: Recurso = {
  id: 'madeira',
  itemComum: 'log',
  ferramentaPadrao: 'hatchet',
  cores: {
    'oak-log': '#c9a06a', 'ash-log': '#d8cfb8', 'yew-log': '#8a6a3a',
    'heartwood-log': '#d9a35a', 'bloodwood-log': '#b8443a', 'frostwood-log': '#9fd3e8'
  },
  t: {
    titulo: 'coleta.titulo',
    lugar: 'coleta.floresta',
    ponto: 'coleta.arvore',
    coletores: 'coleta.lenhadores',
    faseAndando: 'coleta.fase.andando',
    faseCortando: 'coleta.fase.cortando',
    resIda: 'coleta.res.ida',
    resProcurando: 'coleta.res.procurando',
    resAndando: 'coleta.res.andando',
    resErro: 'coleta.res.erro',
    travado: 'coleta.travado',
    skill: 'coleta.lenhador',
    proxima: 'coleta.proxima',
    todas: 'coleta.todas',
    ferramentaDica: 'coleta.machadinhaDica',
    cofreDica: 'coleta.cofreDica',
    itensChance: 'coleta.torasChance',
    trancada: 'coleta.trancada',
    bonus: 'coleta.bonus',
    vjLugar: 'coleta.vjLugar',
    vjAquiPonto: 'coleta.vjAquiArvore',
    temFerramentaBonus: 'coleta.temMachadinhaBonus',
    semFerramenta: 'coleta.semMachadinha',
    resumo: 'coleta.resumo',
    linha: 'hud.viajar.madeira'
  }
};

const MINERIO: Recurso = {
  id: 'minerio',
  itemComum: 'iron-ingot',
  ferramentaPadrao: 'pickaxe',
  // As cores dos metais (Materiais.cs / look/materiais.ts).
  cores: {
    'dull-copper-ingot': '#7a6a55', 'shadow-iron-ingot': '#3f4450', 'copper-ingot': '#b4633a',
    'bronze-ingot': '#8c6b3f', 'golden-ingot': '#c9a227', 'agapite-ingot': '#a86d94',
    'verite-ingot': '#3f8c5a', 'valorite-ingot': '#5a7fb4'
  },
  t: {
    titulo: 'coleta.minerio.titulo',
    lugar: 'coleta.minerio.mina',
    ponto: 'coleta.minerio.veio',
    coletores: 'coleta.minerio.mineradores',
    faseAndando: 'coleta.minerio.fase.andando',
    faseCortando: 'coleta.minerio.fase.minerando',
    resIda: 'coleta.minerio.res.ida',
    resProcurando: 'coleta.minerio.res.procurando',
    resAndando: 'coleta.minerio.res.andando',
    resErro: 'coleta.minerio.res.erro',
    travado: 'coleta.minerio.travado',
    skill: 'coleta.minerio.skill',
    proxima: 'coleta.minerio.proximo',
    todas: 'coleta.minerio.todos',
    ferramentaDica: 'coleta.minerio.picaretaDica',
    cofreDica: 'coleta.minerio.cofreDica',
    itensChance: 'coleta.minerio.lingotesChance',
    trancada: 'coleta.minerio.trancado',
    bonus: 'coleta.minerio.bonus',
    vjLugar: 'coleta.minerio.vjLugar',
    vjAquiPonto: 'coleta.minerio.vjAquiVeio',
    temFerramentaBonus: 'coleta.minerio.temPicaretaBonus',
    semFerramenta: 'coleta.semPicareta',
    resumo: 'coleta.minerio.resumo',
    linha: 'hud.viajar.minerio'
  }
};

export const RECURSOS: readonly Recurso[] = [MINERIO, MADEIRA];

/** O recurso pelo id; qualquer outro valor (estado antigo, fio vazio) é a madeira. */
export function recursoDe(id: string | null | undefined): Recurso {
  return id === 'minerio' ? MINERIO : MADEIRA;
}
